<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Full-screen veil for the synchronous optimize call: with many shifts the request takes
// tens of seconds, so cycle stage messages to keep the wait legible. The stages are
// illustrative pacing, not real progress — the backend reports nothing until it finishes.
interface Props {
  active: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();

const STAGE_KEYS = ['stageSave', 'stageMatrix', 'stageSolve', 'stageCalendar'];
const STAGE_MS = 6000;

const stageIndex = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;

function stopCycling() {
  if (timer) clearInterval(timer);
  timer = null;
}

// Hold on the last stage instead of looping — a loop would read as a restarted run.
watch(() => props.active, (active) => {
  stopCycling();
  if (!active) return;
  stageIndex.value = 0;
  timer = setInterval(() => {
    if (stageIndex.value < STAGE_KEYS.length - 1) stageIndex.value += 1;
    else stopCycling();
  }, STAGE_MS);
}, { immediate: true });

onBeforeUnmount(stopCycling);
</script>

<template>
  <v-overlay
    :model-value="active"
    persistent
    class="align-center justify-center"
  >
    <v-card
      class="pa-8 d-flex flex-column align-center ga-4 text-center"
      max-width="420"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
      />
      <div class="text-h6">
        {{ t('routePlan.optimizeOverlay.title') }}
      </div>
      <div class="text-body-2">
        {{ t(`routePlan.optimizeOverlay.${STAGE_KEYS[stageIndex]}`) }}
      </div>
      <div class="text-caption text-medium-emphasis">
        {{ t('routePlan.optimizeOverlay.hint') }}
      </div>
    </v-card>
  </v-overlay>
</template>
